import React, { useEffect, useRef, useState } from 'react';
import { motion, useScroll, useSpring, useTransform, Variants } from 'framer-motion';
import { Star, Eye, Heart, Activity, Handshake, CheckCircle2, Quote, Microscope, Stethoscope, ShieldCheck, User } from 'lucide-react';
import TiltCard from '../components/TiltCard';
import { stock } from '../data/media';
import { useIsMobile } from '../hooks/useIsMobile';

/** A horizontally-scrolling row that auto-advances on its own, pauses the
 * instant a finger (or pointer) touches it down, and resumes a little while
 * after release. Because it's a real scrollable element (not a transform
 * loop), native touch-swipe / drag-to-scroll always works too, whether the
 * auto-advance is paused or not — that's what makes "touch to stop, swipe to
 * move" work for free instead of needing separate gesture handling. */
const AutoScrollRow: React.FC<{ children: React.ReactNode; speed?: number; style?: React.CSSProperties }> = ({ children, speed = 0.6, style }) => {
  const ref = useRef<HTMLDivElement>(null);
  const paused = useRef(false);
  const resumeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let raf: number;
    const step = () => {
      const el = ref.current;
      if (el) {
        if (!paused.current) el.scrollLeft += speed;
        // Wrap as soon as we cross into the duplicated second copy — checked
        // every frame regardless of pause state, so a manual swipe past this
        // point loops immediately instead of only being caught while the
        // auto-scroll increment itself was running (which is what let it
        // "stop" dead at the true end when a user had swiped there by hand).
        const halfway = (el.scrollWidth - el.clientWidth) / 2;
        if (el.scrollLeft >= halfway) {
          el.scrollLeft -= halfway;
        }
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [speed]);

  const pause = () => {
    paused.current = true;
    if (resumeTimer.current) clearTimeout(resumeTimer.current);
  };
  const resumeSoon = () => {
    if (resumeTimer.current) clearTimeout(resumeTimer.current);
    resumeTimer.current = setTimeout(() => { paused.current = false; }, 2000);
  };

  return (
    <div
      ref={ref}
      onTouchStart={pause}
      onTouchEnd={resumeSoon}
      onMouseDown={pause}
      onMouseUp={resumeSoon}
      className="auto-scroll-row"
      style={{ display: 'flex', overflowX: 'auto', WebkitOverflowScrolling: 'touch', ...style }}
    >
      {children}
    </div>
  );
};

// --- DATA ---
const profileData = {
  name: 'Dr. K. R. Premchand',
  role: 'Managing Director, Reach Groups',
  qualifications: 'MD Physician Kuban State Medical University, Russia (2005) | Physician, Sonologist & Echocardiologist',
  image: stock.founder_bg,
  quote: 'From a single clinical practice in 2008 to Reach Groups today every step has been about reaching more families with the same compassion we started with.',
  bio: "Dr. K. R. Premchand graduated as an MD Physician from Kuban State Medical University, Krasnodar, Russia in 2005, and began his clinical practice in 2008 continuing to serve the community to this day as a Physician, Sonologist and Echocardiologist. He has guided fellow doctors through Tamil Nadu Medical Council–accredited workshops since 2015, and has led social welfare initiatives since 2020, including continuous health services through both waves of the COVID-19 pandemic. Reach Groups was founded in 2026, uniting 15 years of that work from Med In Clinics and Med In Scans & Labs to Reach Multispeciality Clinics & Hospitals under one shared vision: to restore and improve community health.",
  expertise: [
    'MD Physician, Russia (2005)', 'Sonologist & Echocardiologist',
    'Clinical Practice since 2008', 'TN Medical Council Workshops since 2015',
    'Social Welfare Initiatives since 2020', '1,00,000+ Lives Served',
    'COVID-19 Frontline Health Services', 'Reach Groups Founded, 2026'
  ]
};

const team = [
  { name: 'Dr. UMASHANKAR. U. S ', role: 'Senior Consultant - Medical Gastroenterology', bio: 'MBBS, MD | DM - Med. Gastro | PDCC - Endoscopy. Specialist in gastroenterology, hepatology, advanced endoscopy, ERCP, colonoscopy, and minimally invasive gastrointestinal therapeutic procedures.', image: stock.umasankar },
  { name: 'Dr. S. RESHMA', role: 'Neurosurgeon', bio: 'MBBS., MS., DrNB Neurosurgeon. Expert in brain and spine surgery, delivering advanced neurosurgical care with precision, innovation, and a patient-centered approach.', image: stock.reshma },
  { name: 'Dr. R. MUTHTHIAH', role: 'Chief Cardiologist', bio: 'MBBS.MD., GENERAL PHYSICAN', image: stock.muthiah },
  { name: 'Dr. MUTHURENGANATHAN', role: 'Senior Specialist', bio: 'MBBS., MS. Surgeon, Dedicated medical professional committed to providing outstanding patient care and clinical excellence.', image: stock.muthurengan },
  { name: 'Dr. DHARMARAJ', role: 'Chief Cardiologist', bio: 'MD, DM. Expert in advanced cardiac diagnostics, cardiovascular disease management, preventive cardiology, and personalized heart care.', image: stock.dharmaraj },
  { name: 'Dr. SRIRAM', role: 'Senior Consultant & Interventional Cardiologist', bio: 'MBBS, MD, DM (Cardiology). Expert in complex coronary interventions, structural heart procedures, heart failure management, cardiac device implantation, and advanced cardiovascular care.', image: stock.sriram },
  { name: 'Dr. T. SANDRAVELU', role: 'ENT & Sinus Surgeon', bio: 'MBBS, DNB (ENT)', image: stock.sandravelu },
  { name: 'Dr. R. JANANI', role: 'Ophthalmologist', bio: 'MBBS, MS, DNB (Ophthalmology)', image: stock.janani },
  { name: 'Dr. ANNIE SHEENA PAUL', role: 'DNB Psychiatry', bio: 'MD, DNB Psychiatry', image: stock.annieshennapaul },
  { name: 'RESHMA BANU', role: 'Lab Assistant', bio: '', image: stock.reshmabanu },

  // 4 Empty Placeholder Cards
  { name: 'GOWSIYA BEGUM', role: 'Lab Team Incharge', bio: '', image: stock.gowsiya },
  { name: 'ISWARYA', role: 'Lab Team Incharge', bio: '', image: stock.iswarya },
  { name: 'NANDHA KUMAR', role: 'Lab Assistant', bio: '', image: stock.nandha },
  { name: 'C. HEMA LATHA', role: 'Accountant', bio: '', image: stock.hema },
  { name: 'C. PANDEESWARI', role: 'HR', bio: '', image: stock.pandeeswari },

];

const reviews = [
  { name: 'Priya Ramesh', rating: 5, text: 'Excellent service! The diagnostics team was very professional and reports were ready in no time. Highly recommend Reach Groups.', date: '2 weeks ago' },
  { name: 'Karthik Sundaram', rating: 5, text: 'The home nursing service was exceptional. The caretaker was skilled, compassionate and my mother recovered beautifully under their care.', date: '1 month ago' },
  { name: 'Anitha Venkatesh', rating: 5, text: 'Amazing multispeciality clinic. Got same-day appointment with the cardiologist. Clean, modern facility with caring staff.', date: '3 weeks ago' },
];

const journeyMilestones = [
  { num: "01", icon: Heart, title: "Compassion", desc: "Every patient interaction begins with empathy, dignity and personalised attention." },
  { num: "02", icon: Microscope, title: "Diagnosis", desc: "Advanced diagnostic technology enables more accurate clinical decisions." },
  { num: "03", icon: Stethoscope, title: "Treatment", desc: "Evidence-based treatments tailored to every patient's individual condition." },
  { num: "04", icon: Activity, title: "Recovery", desc: "Continuous monitoring and rehabilitation support throughout recovery." },
  { num: "05", icon: ShieldCheck, title: "Lifelong Care", desc: "Preventive healthcare and wellness support that continues beyond treatment." }
];

// --- VARIANTS ---
const staggerContainer: Variants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.12, delayChildren: 0.1 } }
};

const blurReveal: Variants = {
  hidden: { opacity: 0, scale: 0.95, filter: 'blur(8px)', y: 20 },
  show: { opacity: 1, scale: 1, filter: 'blur(0px)', y: 0, transition: { type: 'spring', damping: 20, stiffness: 100 } }
};

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 25 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } }
};

export default function About() {
  const isMobile = useIsMobile();
  const { scrollYProgress } = useScroll();
  const smoothProgress = useSpring(scrollYProgress, { stiffness: 100, damping: 30, restDelta: 0.001 });
  const yParallax = useTransform(smoothProgress, [0, 1], [0, -100]);
  
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [isHoveringNode, setIsHoveringNode] = useState(false);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => setMousePos({ x: e.clientX, y: e.clientY });
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const heroNodes: { id: string; top?: string; bottom?: string; left?: string; right?: string; iconColor: string; icon: typeof Eye; title: string; desc: string; align: 'left' | 'right'; dir: 'row' | 'row-reverse' }[] = [
    { id: 'vision', top: '0%', bottom: undefined, left: '-12%', right: undefined, iconColor: '#005B66', icon: Eye, title: 'Vision', desc: "Restore and improve community health through Reach's health care and non-health care verticals reaching every family with trust, innovation and compassionate care.", align: 'left', dir: 'row-reverse' },
    { id: 'mission', top: '0%', bottom: undefined, left: undefined, right: '-12%', iconColor: '#D62828', icon: Heart, title: 'Mission', desc: 'Unite people with shared purpose under Reach Groups, and expand accessible healthcare and community services as far as we can reach.', align: 'right', dir: 'row' },
    { id: 'values', top: undefined, bottom: '0%', left: '-12%', right: undefined, iconColor: '#005B66', icon: Activity, title: 'Values', desc: '• Compassion\n• Clinical Excellence\n• Integrity\n• Innovation\n• Patient First', align: 'left', dir: 'row-reverse' },
    { id: 'commit', top: undefined, bottom: '0%', left: undefined, right: '-12%', iconColor: '#D62828', icon: Handshake, title: 'Commitment', desc: '• 1,00,000+ lives served\n• Serving Pallavaram since 2008\n• Continuous care through COVID-19\n• Community-first, always', align: 'right', dir: 'row' }
  ];

  return (
    <div className="App" style={{ textAlign: 'center', backgroundColor: '#ffffff', minHeight: '100vh', display: 'flex', flexDirection: 'column', scrollBehavior: 'smooth', overflowX: 'hidden' }}>
      
      {/* GLOBAL CSS & EFFECTS */}
      <style>{`
        /* EXACT TYPOGRAPHY SYSTEM */
        .poppins-heading {
          font-family: 'Poppins', sans-serif;
          font-weight: 700;
          color: #1a1a1a;
          line-height: 1.25;
          letter-spacing: -0.5px;
        }
        .inter-body {
          font-family: 'Inter', sans-serif;
          line-height: 1.6;
          color: #475569;
        }
        .section-tag {
          font-family: 'Inter', sans-serif;
          font-weight: 700;
          font-size: 13px;
          text-transform: uppercase;
          letter-spacing: 2px;
          color: #005B66;
          display: block;
          margin-bottom: 12px;
        }
        .section-title {
          font-family: 'Poppins', sans-serif;
          font-size: clamp(28px, 4vw, 42px);
          font-weight: 700;
          color: #000000;
          line-height: 1.2;
          margin-bottom: 16px;
          letter-spacing: -0.5px;
        }
        .section-subtitle {
          font-family: 'Inter', sans-serif;
          font-size: 17px;
          font-weight: 500;
          color: #475569;
          line-height: 1.6;
        }

        /* AMBIENT EFFECTS */
        .animated-gradient-bg { 
          background: linear-gradient(-45deg, #ffffff, #f0fdfa, #f8fafc, #ffffff); 
          background-size: 200% 200%; 
          animation: gradientBG 15s ease infinite; 
        }
        @keyframes gradientBG { 
          0% { background-position: 0% 50%; } 
          50% { background-position: 100% 50%; } 
          100% { background-position: 0% 50%; } 
        }

        /* HIDE ON MOBILE */
        @media (max-width: 1024px) { 
          .hide-on-mobile { display: none !important; } 
        }

        /* NAVBAR OFFSET FIX */
        .navbar-offset {
          padding-top: 100px;
        }

        /* PREMIUM 5x2 TEAM GRID */
        .team-grid {
          display: grid;
          grid-template-columns: repeat(5, 1fr);
          gap: 28px;
        }
        
        .team-card-hover {
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.03);
          border: 1px solid rgba(0, 0, 0, 0.03);
          transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .team-card-hover:hover {
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08);
          border-color: rgba(0, 91, 102, 0.1);
          transform: translateY(-4px);
        }
        .team-card-img {
          transition: transform 0.5s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .team-card-hover:hover .team-card-img {
          transform: scale(1.08);
        }

        /* RESPONSIVE BREAKPOINTS */
        @media (max-width: 1200px) { .team-grid { grid-template-columns: repeat(4, 1fr); } }
        @media (max-width: 1024px) { .team-grid { grid-template-columns: repeat(3, 1fr); } }
        @media (max-width: 768px) { .team-grid { grid-template-columns: repeat(2, 1fr); } }
        @media (max-width: 480px) { .team-grid { grid-template-columns: 1fr; } }

        /* Auto-scrolling carousels (Patient Care Journey / reviews on mobile) —
           hide the scrollbar since scroll position is driven by touch/JS, not
           a visible scrollbar the user would drag. */
        .auto-scroll-row { scrollbar-width: none; -ms-overflow-style: none; }
        .auto-scroll-row::-webkit-scrollbar { display: none; }

      `}</style>

      {/* Floating Medical Particles (Subtle) — skipped on mobile */}
      {!isMobile && (
        <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0, overflow: 'hidden' }}>
          {[...Array(12)].map((_, i) => (
            <motion.div key={i} style={{ position: 'absolute', left: `${Math.random() * 100}vw`, top: `${Math.random() * 100}vh`, width: Math.random() * 4 + 2, height: Math.random() * 4 + 2, borderRadius: '50%', background: '#005B66', opacity: 0.06, y: yParallax }} animate={{ opacity: [0.03, 0.08, 0.03], scale: [1, 1.2, 1] }} transition={{ duration: 4 + Math.random() * 5, repeat: Infinity, ease: "easeInOut" }} />
          ))}
        </div>
      )}

      {/* Interactive Cursor Spotlight — skipped on mobile/touch, there's no cursor to follow */}
      {!isMobile && (
        <>
          <motion.div style={{ position: 'fixed', top: mousePos.y - 400, left: mousePos.x - 400, width: 800, height: 800, background: 'radial-gradient(circle, rgba(0,91,102,0.03) 0%, rgba(0,0,0,0) 60%)', pointerEvents: 'none', zIndex: 999 }} transition={{ type: "tween", ease: "backOut", duration: 0.5 }} />
          <motion.div animate={{ scale: isHoveringNode ? 2.5 : 1, opacity: isHoveringNode ? 0.2 : 0 }} style={{ position: 'fixed', top: mousePos.y - 15, left: mousePos.x - 15, width: 30, height: 30, borderRadius: '50%', background: '#005B66', pointerEvents: 'none', zIndex: 1000, mixBlendMode: 'multiply' }} transition={{ type: "spring", stiffness: 150, damping: 15 }} />
        </>
      )}
      
      {/* Scroll Progress Indicator */}
      <motion.div style={{ position: 'fixed', top: 0, left: 0, right: 0, height: '4px', background: 'linear-gradient(90deg, #005B66, #D62828)', scaleX: smoothProgress, transformOrigin: '0%', zIndex: 1001 }} />

      <main style={{ flex: 1, position: 'relative', zIndex: 1, overflowX: 'hidden' }} className="animated-gradient-bg">
        
        {/* ========================================================= */}
        {/* 1. HERO SECTION                                           */}
        {/* ========================================================= */}
        <section style={{ height: isMobile ? 'auto' : '100vh', minHeight: isMobile ? 'auto' : '800px', display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', overflow: isMobile ? 'visible' : 'hidden', paddingBottom: isMobile ? 40 : 0 }}>
          
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'stretch', width: '100%', maxWidth: '1600px', gap: '0', height: isMobile ? 'auto' : '100%' }}>
            
            {/* Left Image */}
            <motion.div className="hide-on-mobile" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 2 }} style={{ flex: 1, position: 'relative', height: '100%' }}>
              <div style={{ width: '100%', height: '100%', WebkitMaskImage: 'linear-gradient(to right, rgba(0,0,0,1) 30%, rgba(0,0,0,0) 100%)', maskImage: 'linear-gradient(to right, rgba(0,0,0,1) 30%, rgba(0,0,0,0) 100%)' }}>
                <img src={stock.rmc} alt="Compassionate Care" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }} />
              </div>
            </motion.div>

            {/* Central Column */}
            <div className="navbar-offset" style={{ flex: 1.6, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', position: 'relative', zIndex: 10, width: '100%', maxWidth: '850px', padding: isMobile ? '100px 20px 2vh' : '100px 20px 2vh' }}>
              
              <motion.div variants={staggerContainer} initial="hidden" whileInView="show" viewport={{ margin: "-50px" }} style={{ textAlign: 'center', marginBottom: '30px' }}>
                <motion.h1 variants={blurReveal} className="section-title" style={{ whiteSpace: 'nowrap' }}>
                  About <span style={{ color: '#005B66' }}>Reach Groups</span>
                </motion.h1>
                  <motion.p variants={blurReveal} className="section-subtitle" style={{ maxWidth: '700px', margin: '0 auto' }}>
                  Founded in 2026 to restore and improve community health uniting Reach's health care and non-health care verticals under one shared vision, built on 15 years of clinical practice and community service around Pallavaram.
                  </motion.p>
              </motion.div>

              {/* Logo — on mobile this now sits on its own, directly below the
                  heading, instead of inside the node-system flow where it
                  used to land awkwardly mid-page. */}
              {isMobile && (
                <motion.div variants={blurReveal} style={{ marginBottom: 22, width: '100px', height: '100px', borderRadius: '50%', background: 'linear-gradient(135deg, #005B66, #ffffff)', display: 'flex', justifyContent: 'center', alignItems: 'center', border: '4px solid white', boxShadow: '0 0 40px rgba(214,40,40,0.25)' }}>
                  <img src={stock.mainlogo} alt="RG Logo" style={{ width: '60px', height: '60px', objectFit: 'contain' }} />
                </motion.div>
              )}

              {/* Loosened & Spread Node System */}
              <motion.div initial="hidden" whileInView="show" variants={staggerContainer} viewport={{ margin: "-50px" }} style={{ position: isMobile ? 'static' : 'relative', height: isMobile ? 'auto' : '520px', width: '100%', display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'center', alignItems: 'center', gap: isMobile ? 22 : 0 }}>

                {/* Clean SVG Connections — desktop only, they're positioned
                    against the absolute node layout which mobile doesn't use */}
                {!isMobile && (
                  <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 1 }}>
                    {[ 
                      { x2: '4%', y2: '18%' }, { x2: '96%', y2: '18%' }, 
                      { x2: '4%', y2: '82%' }, { x2: '96%', y2: '82%' } 
                    ].map((line, i) => (
                      <g key={i}>
                        <motion.line x1="50%" y1="50%" x2={line.x2} y2={line.y2} stroke="rgba(0, 91, 102, 0.15)" strokeWidth="1.5" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1.5, ease: "easeInOut", delay: 0.2 }} />
                        <motion.line x1="50%" y1="50%" x2={line.x2} y2={line.y2} stroke="url(#pulseGradTeal)" strokeWidth="2" initial={{ pathLength: 0, opacity: 0 }} animate={{ pathLength: [0, 1], opacity: [0, 0.8, 0] }} transition={{ duration: 3, repeat: Infinity, ease: "linear", delay: i * 0.4 + 1 }} />
                      </g>
                    ))}
                    <defs>
                      <linearGradient id="pulseGradTeal" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#005B66" stopOpacity="0" />
                        <stop offset="50%" stopColor="#005B66" stopOpacity="1" />
                        <stop offset="100%" stopColor="#005B66" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                  </svg>
                )}

                {/* Central Core (Subtle Pulse) — desktop only now; mobile
                    renders its own static logo block above instead */}
                {!isMobile && (
                  <div style={{ position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 5 }}>
                    <motion.div animate={{ scale: [1, 1.4, 2], opacity: [0.3, 0.1, 0] }} transition={{ duration: 3, repeat: Infinity, ease: "easeOut" }} style={{ position: 'absolute', width: '90px', height: '90px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(214,40,40,0.3) 0%, transparent 70%)' }} />
                    <motion.div variants={blurReveal} animate={{ boxShadow: ["0 0 60px rgba(214,40,40,0.2)", "0 0 80px rgba(214,40,40,0.4)", "0 0 60px rgba(214,40,40,0.2)"] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }} style={{ width: '100px', height: '100px', borderRadius: '50%', background: 'linear-gradient(135deg, #005B66, #ffffff)', display: 'flex', justifyContent: 'center', alignItems: 'center', border: '4px solid white', position: 'relative', zIndex: 2 }}>
                      <img src={stock.mainlogo} alt="RG Logo" style={{ width: '60px', height: '60px', objectFit: 'contain' }} />
                    </motion.div>
                  </div>
                )}

                {/* Floating Glass Nodes (Premium Interactions) — on mobile these
                    render as a plain stacked list instead of 4 absolutely-
                    positioned 380px-wide cards, which is what was causing them
                    to pile on top of each other and the logo on a narrow screen. */}
                {isMobile ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14, width: '100%' }}>
                    {heroNodes.map(node => (
                      <div key={node.id} style={{
                        display: 'flex', alignItems: 'flex-start', gap: 14, textAlign: 'left',
                        background: 'rgba(255,255,255,0.95)', border: '1px solid rgba(226, 232, 240, 0.8)',
                        borderRadius: 20, padding: '16px', boxShadow: '0 10px 24px rgba(0,0,0,0.05)',
                      }}>
                        <div style={{ width: 44, height: 44, borderRadius: '50%', background: `linear-gradient(135deg, ${node.iconColor}, ${node.iconColor}dd)`, display: 'flex', justifyContent: 'center', alignItems: 'center', flexShrink: 0 }}>
                          <node.icon color="white" size={20} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <h3 className="poppins-heading" style={{ margin: '0 0 4px 0', fontSize: '0.98rem', color: '#000000' }}>{node.title}</h3>
                          <p className="inter-body" style={{ fontSize: '0.8rem', margin: 0, lineHeight: 1.5, whiteSpace: 'pre-line' }}>{node.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  heroNodes.map((node, i) => (
                    <motion.div 
                      key={node.id} 
                      onMouseEnter={() => setIsHoveringNode(true)} 
                      onMouseLeave={() => setIsHoveringNode(false)} 
                      variants={blurReveal} 
                      style={{ position: 'absolute', top: node.top, bottom: node.bottom, left: node.left, right: node.right, zIndex: 10, width: '380px' }}
                    >
                      <motion.div 
                        /* Premium Idle Breathing Float */
                        animate={{ y: [0, -2, 0] }} 
                        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: i * 0.5 }} 
                        /* Calm Premium Hover */
                        whileHover={{ 
                          scale: 1.02, 
                          y: -8, 
                          boxShadow: '0 25px 50px rgba(0,91,102,0.12)',
                          borderColor: 'rgba(0, 91, 102, 0.4)',
                          backgroundColor: 'rgba(255,255,255,0.98)'
                        }} 
                        style={{ 
                          display: 'flex', alignItems: 'flex-start', gap: '16px', flexDirection: node.dir as any, 
                          background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', 
                          padding: '20px', borderRadius: '24px', border: '1px solid rgba(226, 232, 240, 0.8)', 
                          cursor: 'pointer', transition: 'all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1)',
                          boxShadow: '0 10px 30px rgba(0,0,0,0.03)'
                        }}
                      >
                        <div style={{ position: 'relative', width: '52px', height: '52px', borderRadius: '50%', background: `linear-gradient(135deg, ${node.iconColor}, ${node.iconColor}dd)`, display: 'flex', justifyContent: 'center', alignItems: 'center', flexShrink: 0, boxShadow: `0 8px 20px ${node.iconColor}30` }}>
                          {/* Hover Halo */}
                          <motion.div 
                            style={{ position: 'absolute', inset: -4, borderRadius: '50%', border: `2px solid ${node.iconColor}`, opacity: 0 }}
                            whileHover={{ opacity: 0.3, scale: 1.1, transition: { duration: 0.3 } }}
                          />
                          <node.icon color="white" size={22} style={{ position: 'relative', zIndex: 2 }} />
                        </div>
                        <div style={{ textAlign: node.align as any, flex: 1 }}>
                          <h3 className="poppins-heading" style={{ margin: '0 0 6px 0', fontSize: '1.15rem', color: '#000000' }}>{node.title}</h3>
                          <p className="inter-body" style={{ fontSize: '0.85rem', margin: 0, lineHeight: 1.5, whiteSpace: 'pre-line' }}>{node.desc}</p>
                        </div>
                      </motion.div>
                    </motion.div>
                  ))
                )}
              </motion.div>
            </div>

            {/* Right Image */}
            <motion.div className="hide-on-mobile" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 2, delay: 0.3 }} style={{ flex: 1, position: 'relative', height: '100%' }}>
              <div style={{ width: '100%', height: '100%', WebkitMaskImage: 'linear-gradient(to left, rgba(0,0,0,1) 30%, rgba(0,0,0,0) 100%)', maskImage: 'linear-gradient(to left, rgba(0,0,0,1) 30%, rgba(0,0,0,0) 100%)' }}>
                <img src={stock.rmsl} alt="Advanced Diagnosis" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }} />
              </div>
            </motion.div>
            
          </div>
        </section>

        {/* ========================================================= */}
        {/* 2. THE PATIENT CARE JOURNEY (Visual Process Pathway)      */ }
        {/* ========================================================= */}
        <section style={{ padding: '8vh 20px', background: 'transparent', position: 'relative' }}>
          <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative' }}>
            
            <motion.div initial="hidden" whileInView="show" variants={staggerContainer} viewport={{ margin: "-50px" }} style={{ textAlign: 'center', marginBottom: '60px' }}>
              <motion.h2 variants={fadeUp} className="section-title">The Patient Care Journey</motion.h2>
              <motion.p variants={fadeUp} className="section-subtitle" style={{ margin: '0 auto', maxWidth: '600px' }}>A seamless, connected pathway dedicated to your recovery and lifelong health.</motion.p>
            </motion.div>

            {/* Visually Connected Pathway Container */}
            <motion.div 
              initial="hidden" whileInView="show" variants={staggerContainer} viewport={{ margin: "-50px" }} 
              style={{ position: 'relative', zIndex: 1, padding: '20px 0' }}
            >
              {isMobile ? (
                /* Auto-advancing carousel: moves on its own, stops the instant
                   it's touched, and can always be swiped manually (native
                   scroll) whether it's paused or not — see AutoScrollRow. */
                <AutoScrollRow speed={0.5} style={{ gap: 18, padding: '4px 4px 10px' }}>
                  {[...journeyMilestones, ...journeyMilestones].map((ms, i) => (
                    <div key={i} style={{
                      flexShrink: 0, width: 250, background: '#ffffff', borderRadius: 20,
                      border: '1px solid #e2e8f0', boxShadow: '0 8px 20px rgba(0,0,0,0.04)',
                      padding: '28px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center',
                    }}>
                      <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#005B66', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontFamily: "'Poppins', sans-serif", fontSize: 19, marginBottom: 14, flexShrink: 0 }}>
                        {ms.num}
                      </div>
                      <ms.icon size={28} color="#005B66" style={{ marginBottom: 12 }} />
                      <h4 className="poppins-heading" style={{ fontSize: '1.15rem', margin: '0 0 8px 0' }}>{ms.title}</h4>
                      <p className="inter-body" style={{ fontSize: '0.9rem', margin: 0, lineHeight: 1.55 }}>{ms.desc}</p>
                    </div>
                  ))}
                </AutoScrollRow>
              ) : (
                <>
                  {/* Sequential Connection Pathway with Progress Draw */}
                  <div style={{ position: 'absolute', top: '130px', left: '0', right: '0', height: '180px', pointerEvents: 'none' }}>
                    <svg width="100%" height="100%" viewBox="0 0 1200 180" preserveAspectRatio="none">
                      {/* Background Path */}
                      <motion.path 
                        d="M 60 40 C 200 40, 200 140, 300 140 C 400 140, 400 40, 540 40 C 680 40, 680 140, 840 140 C 1000 140, 1000 40, 1140 40"
                        stroke="#e2e8f0" strokeWidth="3" fill="none"
                      />
                      {/* Progress Draw Line */}
                      <motion.path 
                        d="M 60 40 C 200 40, 200 140, 300 140 C 400 140, 400 40, 540 40 C 680 40, 680 140, 840 140 C 1000 140, 1000 40, 1140 40"
                        stroke="#005B66" strokeWidth="3" fill="none"
                        initial={{ pathLength: 0 }}
                        whileInView={{ pathLength: 1 }}
                        transition={{ duration: 2, ease: "easeInOut", delay: 0.2 }}
                      />
                    </svg>
                  </div>
                  
                  {/* Process Stages Grid */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '10px', alignItems: 'flex-start' }}>
                    {journeyMilestones.map((ms, i) => (
                      <motion.div 
                        key={i} variants={fadeUp} 
                        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', zIndex: 10, cursor: 'pointer' }}
                      >
                        
                        {/* Visual Checkpoint Node */}
                        <div style={{ position: 'relative', marginBottom: '24px', width: '100px', height: '100px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                          
                          {/* Sequential Expand Ripple */}
                          <motion.div 
                            initial={{ scale: 0, opacity: 0 }}
                            whileInView={{ scale: 1, opacity: [0, 0.4, 0] }}
                            transition={{ duration: 1.2, ease: "easeOut", delay: 0.5 + (i * 0.3) }}
                            style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: '#005B66', zIndex: 1 }}
                          />
                          
                          {/* Checkpoint Circle */}
                          <motion.div 
                            initial={{ scale: 0 }}
                            whileInView={{ scale: 1 }}
                            transition={{ type: "spring", stiffness: 120, damping: 15, delay: 0.5 + (i * 0.3) }}
                            style={{ width: '80px', height: '80px', borderRadius: '50%', display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#005B66', border: '6px solid #ffffff', boxShadow: '0 8px 25px rgba(0,91,102,0.25)', color: '#ffffff', fontWeight: 700, fontSize: '1.25rem', fontFamily: "'Poppins', sans-serif", position: 'relative', zIndex: 2 }}
                          >
                            {ms.num}
                          </motion.div>
                        </div>
                        
                        {/* Progress Flow Arrow (Subtle Process Visual) */}
                        {i < journeyMilestones.length - 1 && (
                          <div style={{ position: 'absolute', top: '70px', right: '-15%', zIndex: 0, color: '#e2e8f0' }}>
                            <svg width="40" height="20" viewBox="0 0 40 20">
                              <path d="M 0 10 L 35 10 L 25 2 L 27 0 L 40 10 L 27 20 L 25 18 L 35 10" fill="currentColor"/>
                            </svg>
                          </div>
                        )}

                        {/* Checkpoint Supporting Visual Icon */}
                        <motion.div 
                          initial={{ scale: 0, opacity: 0 }}
                          whileInView={{ scale: 1, opacity: 1 }}
                          transition={{ delay: 0.7 + (i * 0.3) }}
                          style={{ marginBottom: '16px', color: '#005B66' }}
                        >
                          <motion.div animate={{ scale: [1, 1.05, 1] }} transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: i * 0.2 }}>
                            <ms.icon size={26} />
                          </motion.div>
                        </motion.div>

                        {/* Integrated Process Card */}
                        <motion.div 
                          whileHover={{ y: -6, boxShadow: '0 15px 40px rgba(0,0,0,0.06)' }}
                          initial={{ opacity: 0 }}
                          whileInView={{ opacity: 1 }}
                          transition={{ duration: 0.5, delay: 0.8 + (i * 0.3) }}
                          style={{ background: '#ffffff', padding: '24px 16px', borderRadius: '20px', border: '1px solid #e2e8f0', boxShadow: '0 10px 30px rgba(0,0,0,0.03)', width: '100%', backdropFilter: 'blur(10px)', position: 'relative', overflow: 'hidden', transition: 'all 0.3s cubic-bezier(0.165, 0.84, 0.44, 1)' }}
                        >
                          <h4 className="poppins-heading" style={{ fontSize: '1.1rem', margin: '0 0 8px 0', textAlign: 'center', color: '#000000' }}>{ms.title}</h4>
                          <p className="inter-body" style={{ fontSize: '0.85rem', margin: 0, textAlign: 'center', lineHeight: 1.5 }}>{ms.desc}</p>
                        </motion.div>
                        
                      </motion.div>
                    ))}
                  </div>
                </>
              )}
            </motion.div>
          </div>
        </section>

{/* ========================================================= */}
        {/* 3. EXECUTIVE FOUNDER PROFILE (Compact & Contained)        */}
        {/* ========================================================= */}
        <section style={{ padding: '8vh 20px', background: '#f8fafc', position: 'relative', overflow: 'hidden' }}>
          {/* Ambient Background Glow (Continuous) — skipped on mobile */}
          {!isMobile && (
            <>
              <motion.div animate={{ opacity: [0.3, 0.5, 0.3], scale: [1, 1.05, 1] }} transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }} style={{ position: 'absolute', top: '10%', left: '10%', width: '400px', height: '400px', background: 'radial-gradient(circle, rgba(0,91,102,0.05) 0%, transparent 70%)', filter: 'blur(40px)', zIndex: 0 }} />
              {/* Second glow, opposite corner, brand red — balances the teal one */}
              <motion.div animate={{ opacity: [0.25, 0.45, 0.25], scale: [1, 1.08, 1] }} transition={{ duration: 9, repeat: Infinity, ease: "easeInOut", delay: 1.5 }} style={{ position: 'absolute', bottom: '5%', right: '8%', width: '360px', height: '360px', background: 'radial-gradient(circle, rgba(214,40,40,0.06) 0%, transparent 70%)', filter: 'blur(40px)', zIndex: 0 }} />
            </>
          )}
          {/* Faint dot-grid texture across the whole section */}
          <div style={{ position: 'absolute', inset: 0, zIndex: 0, opacity: 0.5, backgroundImage: 'radial-gradient(rgba(0,91,102,0.10) 1.5px, transparent 1.5px)', backgroundSize: '26px 26px', WebkitMaskImage: 'radial-gradient(ellipse 70% 70% at 50% 50%, #000 40%, transparent 85%)', maskImage: 'radial-gradient(ellipse 70% 70% at 50% 50%, #000 40%, transparent 85%)' }} />

          <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 10 }}>

            <motion.div initial="hidden" whileInView="show" variants={staggerContainer} viewport={{ margin: "-50px" }} style={{ textAlign: 'center', marginBottom: '50px' }}>
              <motion.h2 variants={fadeUp} className="section-title" style={{ margin: 0 }}>Meet the Founder</motion.h2>
            </motion.div>
            
             <motion.div initial="hidden" whileInView="show" variants={staggerContainer} viewport={{ margin: "-50px" }} className="premium-card" style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(350px, 1fr))', background: '#ffffff', borderRadius: '24px', border: '1px solid #e2e8f0', padding: isMobile ? '24px 18px' : '40px', gap: isMobile ? 28 : 50, alignItems: 'start', boxShadow: '0 15px 40px rgba(0,0,0,0.04)', position: 'relative', overflow: 'hidden' }}>
               {/* Thin brand gradient accent across the top of the card itself */}
               <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '4px', background: 'linear-gradient(90deg, #005B66, #D62828)' }} />

               {/* LEFT — image, then name/role/qualifications directly below
                   it (previously on the right, which is what left this
                   column looking short with a lot of empty space beneath
                   the image). */}
               <div style={{ width: '100%', maxWidth: '400px', margin: '0 auto' }}>
                 <motion.div variants={fadeUp}>
                   <img src={profileData.image} alt={profileData.name} style={{ width: '100%', height: 'auto', objectFit: 'cover', objectPosition: 'top center', borderRadius: '12px' }} />
                 </motion.div>

                 <motion.h2 variants={fadeUp} className="section-title" style={{ fontSize: 'clamp(1.7rem, 3vw, 2.1rem)', margin: '20px 0 10px 0', textAlign: 'center' }}>
                   {profileData.name}
                 </motion.h2>

                 <motion.div variants={fadeUp} style={{ textAlign: 'center' }}>
                   <span style={{ display: 'inline-block', background: '#f0fdfa', border: '1px solid #ccfbf1', padding: '6px 16px', borderRadius: '100px', fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1.5px', color: '#005B66', marginBottom: '12px' }}>
                     {profileData.role}
                   </span>
                 </motion.div>

                 <motion.p variants={fadeUp} className="inter-body" style={{ fontSize: '0.9rem', color: '#64748b', fontWeight: 500, textAlign: 'center' }}>
                   {profileData.qualifications}
                 </motion.p>
               </div>
               
               {/* RIGHT — quote, bio, Milestones */}
               <div style={{ textAlign: 'left', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', position: 'relative', zIndex: 1 }}>

                  <motion.div variants={fadeUp} style={{ marginBottom: '24px' }}>
                    <p className="inter-body" style={{ fontSize: '18px', lineHeight: 1.7, margin: 0, fontStyle: 'italic', color: '#000000' }}>
                      "{profileData.quote}"
                    </p>
                  </motion.div>

                  <motion.p variants={fadeUp} className="inter-body" style={{ fontSize: '0.92rem', lineHeight: 1.75, marginBottom: '28px', textAlign: isMobile ? 'justify' : 'left' }}>
                    {profileData.bio}
                  </motion.p>

                  <motion.h3 variants={fadeUp} className="poppins-heading" style={{ fontSize: '1.1rem', marginBottom: '16px' }}>
                    Milestones
                  </motion.h3>

                  <motion.div variants={fadeUp} style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: isMobile ? '10px' : '12px 24px', width: '100%' }}>
                    {profileData.expertise.map((item, idx) => (
                      <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                        <CheckCircle2 size={16} color="#005B66" style={{ flexShrink: 0, marginTop: '2px' }} />
                        <span className="inter-body" style={{ fontSize: '0.9rem', color: '#475569', margin: 0, fontWeight: 500 }}>{item}</span>
                      </div>
                    ))}
                  </motion.div>
               </div>
             </motion.div>
          </div>
        </section>

        {/* ========================================================= */}
        {/* 4. PREMIUM TEAM CARDS (with Tilt Interaction)             */}
        {/* ========================================================= */}
        <section style={{ padding: '8vh 20px', background: '#ffffff', position: 'relative' }}>
          <div className="container" style={{ maxWidth: '1600px', margin: '0 auto', padding: '0 4vw' }}>
            
            <motion.div initial="hidden" whileInView="show" variants={staggerContainer} viewport={{ margin: "-50px" }} style={{ textAlign: 'center', marginBottom: '50px' }}>
              <motion.h2 variants={fadeUp} className="section-title" style={{ margin: 0 }}>Meet the Leading Experts</motion.h2>
            </motion.div>
            
            <motion.div initial="hidden" whileInView="show" variants={staggerContainer} viewport={{ margin: "-50px" }} className="team-grid">
              {team.map((member, idx) => (
                <motion.div key={idx} variants={fadeUp} style={{ height: '100%' }}>
                  <TiltCard maxTilt={5} scale={1.02} className="tilt-parent" style={{ height: '100%' }}>
                    <div className="team-card-hover" style={{ position: 'relative', overflow: 'hidden', padding: '40px 20px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer', background: '#ffffff4e', borderRadius: '20px', height: '100%' }}>
                      
                      {/* Subtle Continuous Avatar Breathing / Icon Placeholder — skipped on mobile (15 of these running at once adds up) */}
                      <motion.div animate={isMobile ? undefined : { scale: [1, 1.03, 1] }} transition={isMobile ? undefined : { duration: 5, repeat: Infinity, ease: "easeInOut", delay: idx * 0.5 }} style={{ width: '136px', height: '136px', borderRadius: '50%', marginBottom: '20px', background: '#f8fafc00', border: '1px solid rgba(0,0,0,0.04)', overflow: 'hidden', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                         {member.image ? (
                           <img src={member.image} alt={member.name} className="team-card-img" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                         ) : (
                           <User size={52} color="#cbd5e1" />
                         )}
                      </motion.div>
                      
                      {/* Name or Empty Space */}
                      {member.name ? (
                        <h3 className="poppins-heading" style={{ fontSize: '1.15rem', margin: '0 0 6px 0', letterSpacing: '-0.2px' }}>{member.name}</h3>
                      ) : (
                        <div style={{ height: '1.5rem', marginBottom: '6px' }} />
                      )}
                      
                      {/* Role or Empty Space */}
                      {member.role ? (
                        <div style={{ marginBottom: '14px' }}>
                          <p className="inter-body" style={{ color: '#005B66', fontSize: '0.8rem', fontWeight: 700, margin: 0, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{member.role}</p>
                        </div>
                      ) : (
                        <div style={{ height: '1.2rem', marginBottom: '14px' }} />
                      )}
                      
                      {/* Bio */}
                      <p className="inter-body" style={{ fontSize: '0.9rem', margin: 0, lineHeight: 1.55, color: '#64748b' }}>{member.bio}</p>
                    </div>
                  </TiltCard>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* ========================================================= */}
        {/* 5. RICHER INFINITE REVIEWS CAROUSEL                       */}
        {/* ========================================================= */}
        <section style={{ padding: '8vh 0', background: '#f8fafc', overflow: 'hidden' }}>
          <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px', textAlign: 'center', marginBottom: '50px' }}>
            <motion.div initial="hidden" whileInView="show" variants={staggerContainer} viewport={{ margin: "-50px" }}>
              <motion.h2 variants={fadeUp} className="section-title" style={{ margin: '0 0 12px 0' }}>
                What Our Patients Say
              </motion.h2>
              <motion.div variants={fadeUp} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
                <span style={{ color: '#f59e0b', display: 'flex', gap: 4 }}>
                  {[...Array(5)].map((_, i) => (
                    <motion.div key={i} animate={{ opacity: [0.7, 1, 0.7] }} transition={{ duration: 2, repeat: Infinity, delay: i * 0.2 }}>
                      <Star size={24} fill="#f59e0b" strokeWidth={0} />
                    </motion.div>
                  ))}
                </span>
                <span className="poppins-heading" style={{ fontSize: '1.8rem', margin: 0 }}>4.8</span>
                <span className="inter-body" style={{ margin: 0, fontWeight: 600 }}>on Google Reviews</span>
              </motion.div>
            </motion.div>
          </div>
          
          <AutoScrollRow speed={isMobile ? 0.7 : 0.9} style={{ gap: isMobile ? 20 : 32, padding: isMobile ? '4px 20px 10px' : '4px 32px 10px' }}>
            {[...reviews, ...reviews].map((r, idx) => (
              <div key={idx} className="premium-card" style={{ width: isMobile ? 300 : 400, flexShrink: 0, background: '#ffffff', borderRadius: isMobile ? 20 : 24, padding: isMobile ? 24 : 32, textAlign: 'left', border: '1px solid #e2e8f0', position: 'relative', boxShadow: '0 10px 30px rgba(0,0,0,0.02)', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: isMobile ? 18 : 24, right: isMobile ? 18 : 24, color: '#005B66', opacity: 0.1 }}>
                  <Quote size={isMobile ? 40 : 48} fill="currentColor" strokeWidth={0} />
                </div>
                <div style={{ display: 'flex', gap: 4, marginBottom: isMobile ? 14 : 20, position: 'relative', zIndex: 2 }}>
                  {[...Array(r.rating)].map((_, s) => (
                    <Star key={s} size={isMobile ? 15 : 18} fill="#f59e0b" color="#f59e0b" />
                  ))}
                </div>
                <p className="inter-body" style={{ fontSize: isMobile ? '0.88rem' : '1rem', marginBottom: isMobile ? 18 : 24, fontStyle: 'italic', position: 'relative', zIndex: 2, lineHeight: 1.55 }}>"{r.text}"</p>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #e2e8f0', paddingTop: isMobile ? 14 : 20 }}>
                  <div>
                    <div className="poppins-heading" style={{ fontSize: isMobile ? '0.88rem' : '1rem', margin: 0 }}>{r.name}</div>
                    <div className="inter-body" style={{ fontSize: isMobile ? '0.75rem' : '0.85rem', marginTop: 2 }}>{r.date}</div>
                  </div>
                  <span style={{ color: '#005B66', fontSize: isMobile ? '0.65rem' : '0.75rem', background: '#e0f2fe', padding: isMobile ? '5px 10px' : '6px 12px', borderRadius: isMobile ? 10 : 12, fontWeight: 700, letterSpacing: '0.5px' }}>GOOGLE</span>
                </div>
              </div>
            ))}
          </AutoScrollRow>
        </section>
        
        <div style={{ height: '100px', background: 'linear-gradient(to bottom, #f8fafc, #ffffff)' }} />

      </main>
    </div>
  );
}